import { useMemo, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { AlertTriangle, Info, Timer } from "lucide-react";
import { toast } from "sonner";

/**
 * Beta trial reminder ladder.
 *
 * Shows a sticky banner across the top of the app once `user.beta_ends_at`
 * gets close. Three severity tiers so nothing is missed but the ≥15-day
 * majority isn't nagged.
 *
 *   ≤ 14 days → teal info    "Beta ends in N days · rolls back to Free"
 *   ≤ 7 days  → amber warn   Same copy, more urgent look
 *   ≤ 3 days  → rose critical + a login-time toast (once per day)
 *
 * Nothing renders when there's no `beta_ends_at`, the trial has already
 * flipped over, or the user is on a paid plan.
 */
export default function BetaTrialBanner() {
  const { user } = useAuth();

  const info = useMemo(() => {
    if (!user?.beta_ends_at) return null;
    // Don't nag paid subscribers.
    if (user.plan === "solo" || user.plan === "clinic") {
      if (user.subscription_status === "active" || user.subscription_status === "trialing") return null;
    }
    const endMs = new Date(user.beta_ends_at).getTime();
    if (Number.isNaN(endMs)) return null;
    const diffMs = endMs - Date.now();
    const daysLeft = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    if (daysLeft > 14) return null;
    let tone = "info";
    if (daysLeft <= 3) tone = "critical";
    else if (daysLeft <= 7) tone = "warn";
    return { daysLeft, tone };
  }, [user]);

  // One-per-day login toast when critical. localStorage key includes the trial
  // end date so re-activating the trial re-arms the toast automatically.
  useEffect(() => {
    if (!info || info.tone !== "critical" || !user?.beta_ends_at) return;
    const key = `discrimina.beta_toast.${user.beta_ends_at.slice(0, 10)}`;
    const today = new Date().toISOString().slice(0, 10);
    if (localStorage.getItem(key) === today) return;
    localStorage.setItem(key, today);
    toast.warning(
      `Beta ends in ${info.daysLeft} day${info.daysLeft === 1 ? "" : "s"} — you'll roll back to the Free plan.`,
      { duration: 8000 },
    );
  }, [info, user?.beta_ends_at]);

  if (!info) return null;

  const styles = {
    info:     { bg: "bg-teal-50",  border: "border-teal-200",  text: "text-teal-900",  Icon: Timer,          iconClass: "text-teal-600" },
    warn:     { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-900", Icon: AlertTriangle,  iconClass: "text-amber-600" },
    critical: { bg: "bg-rose-50",  border: "border-rose-300",  text: "text-rose-900",  Icon: AlertTriangle,  iconClass: "text-rose-600" },
  }[info.tone];

  const { Icon } = styles;
  return (
    <div
      className={`${styles.bg} ${styles.border} ${styles.text} border-b px-4 py-2 flex items-center gap-3 text-xs sm:text-sm`}
      data-testid={`beta-trial-banner-${info.tone}`}
    >
      <Icon className={`w-4 h-4 shrink-0 ${styles.iconClass}`} />
      <div className="flex-1 leading-snug">
        <span className="font-semibold">
          {info.daysLeft <= 0
            ? "Beta trial ends today"
            : `Beta ends in ${info.daysLeft} day${info.daysLeft === 1 ? "" : "s"}`}
        </span>
        <span className="opacity-80">
          {" "}· your account stays but rolls back to Free (1 learner · 25 cards).
        </span>
      </div>
      <Link
        to="/billing"
        className="text-[11px] font-bold uppercase tracking-wider underline underline-offset-4 shrink-0"
        data-testid="beta-trial-banner-cta"
      >
        Details →
      </Link>
    </div>
  );
}
